import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { startSession, getWebSocketUrl } from '../services/api';
import { useWebSocket } from '../hooks/useWebSocket';

/**
 * Session Page — handles the full exam session lifecycle:
 * - Reading phase (scenario sessions)
 * - Question/Answer chat interface
 * - Timer display
 * - Submit with confirmation
 * - Disconnect/reconnect handling
 */
function SessionPage({ embedded = false, sessionId: propSessionId }) {
  const params = useParams();
  const sessionId = propSessionId || parseInt(params.sessionId);

  // Session state
  const [phase, setPhase] = useState('loading'); // loading, reading, questions, complete, error
  const [scenario, setScenario] = useState(null);
  const [customerName, setCustomerName] = useState('Customer');
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [inputText, setInputText] = useState('');
  const [firstMessageReceived, setFirstMessageReceived] = useState(false);
  const [timer, setTimer] = useState(0);
  const [readingTimer, setReadingTimer] = useState(0);
  const [questionNumber, setQuestionNumber] = useState(1);
  const [totalQuestions, setTotalQuestions] = useState(40);
  const [isLast, setIsLast] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [completionData, setCompletionData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [timerWarning, setTimerWarning] = useState('');

  // Refs
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const draftRef = useRef('');
  const phaseRef = useRef('loading');
  const timerStartedRef = useRef(false);

  // Typewriter effect state
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const typewriterRef = useRef(null);

  // Disable copy, paste, cut, and context menu during exam
  useEffect(() => {
    const blockEvent = (e) => {
      e.preventDefault();
      return false;
    };

    const blockKeyboard = (e) => {
      // Block Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+A
      if ((e.ctrlKey || e.metaKey) && ['c', 'v', 'x', 'a'].includes(e.key.toLowerCase())) {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener('copy', blockEvent);
    document.addEventListener('paste', blockEvent);
    document.addEventListener('cut', blockEvent);
    document.addEventListener('contextmenu', blockEvent);
    document.addEventListener('keydown', blockKeyboard);

    return () => {
      document.removeEventListener('copy', blockEvent);
      document.removeEventListener('paste', blockEvent);
      document.removeEventListener('cut', blockEvent);
      document.removeEventListener('contextmenu', blockEvent);
      document.removeEventListener('keydown', blockKeyboard);
    };
  }, []);

  // New session popup state
  const [showNewSessionPopup, setShowNewSessionPopup] = useState(false);
  const [nextSessionId, setNextSessionId] = useState(null);
  const newSessionTimerRef = useRef(null);

  // WebSocket
  const wsUrl = getWebSocketUrl(sessionId);
  const { isConnected, lastMessage, reconnectAttempt, connect, sendMessage, close } = useWebSocket(wsUrl);

  // Keep phaseRef in sync
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  // Listen for cross-tab localStorage changes to dismiss popup if accepted elsewhere
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key && e.key.startsWith('session_accepted_') && e.newValue === 'true') {
        // Another tab accepted the popup — hide it here
        setShowNewSessionPopup(false);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Initialize session
  useEffect(() => {
    let mounted = true;
    let initTimeout = null;

    const initSession = async () => {
      try {
        const result = await startSession(sessionId);
        if (!mounted) return;

        if (result.error) {
          setErrorMessage(result.error);
          setPhase('error');
          return;
        }

        if (result.scenario) {
          setScenario(result.scenario);
          if (result.scenario.customerName) {
            setCustomerName(result.scenario.customerName);
          }
        }

        // Store exam time for when candidate clicks Start (don't set timer yet — wait for questions phase)
        if (result.exam_time_seconds) {
          // Timer will be set by the first 'question' message from server after clicking Start
        }

        // Connect WebSocket after session is initialized
        connect();

        // Safety timeout: if still in 'loading' after 30s, show a retry option
        initTimeout = setTimeout(() => {
          if (mounted && phaseRef.current === 'loading') {
            setErrorMessage('Session is taking longer than expected to start. The AI model may be loading. Please wait or refresh the page.');
            setPhase('error');
          }
        }, 30000);
      } catch (err) {
        if (!mounted) return;
        setErrorMessage('Failed to start session. Please try again.');
        setPhase('error');
      }
    };

    initSession();

    return () => {
      mounted = false;
      if (initTimeout) clearTimeout(initTimeout);
    };
    // eslint-disable-next-line
  }, [sessionId]);

  // New session popup — triggered by server WebSocket message
  // Server sends "new_session_available" to the active session's WebSocket

  const handleAcceptNewSession = () => {
    setShowNewSessionPopup(false);
    // Mark as accepted in localStorage so other tabs don't show it again
    localStorage.setItem(`session_accepted_${nextSessionId}`, 'true');
    // Notify server so it stops sending popups across all sessions
    sendMessage({ type: 'popup_accepted', nextSessionId });
    window.open(`${process.env.PUBLIC_URL || ''}/examportal/session/${nextSessionId}`, '_blank');
  };

  const handleDismissNewSession = () => {
    // Dismiss but it will open anyway after a short delay (can't skip sessions)
    setShowNewSessionPopup(false);
    // Re-show after 30 seconds if they dismiss
    setTimeout(() => {
      if (!localStorage.getItem(`session_accepted_${nextSessionId}`)) {
        setShowNewSessionPopup(true);
      }
    }, 30000);
  };

  // Process WebSocket messages
  useEffect(() => {
    if (!lastMessage) return;

    switch (lastMessage.type) {
      case 'reading_phase_start':
        setPhase('reading');
        setReadingTimer(lastMessage.remainingSeconds);
        if (lastMessage.scenario) {
          setScenario(lastMessage.scenario);
          if (lastMessage.scenario.customerName) {
            setCustomerName(lastMessage.scenario.customerName);
          }
        }
        break;

      case 'timer_tick':
        if (lastMessage.phase === 'reading') {
          setReadingTimer(lastMessage.remainingSeconds);
        }
        break;

      case 'timer_start':
        // Server sends this immediately when candidate clicks Start
        // Start the timer before waiting for LLM to generate first question
        setTimer(lastMessage.examTimeRemaining);
        timerStartedRef.current = true;
        break;

      case 'reading_complete':
        // Reading time expired — auto-start the questions phase
        handleReadyClick();
        break;

      case 'ready_for_input':
        // Agent-first flow: session ready, no customer message yet. Agent types first.
        setPhase('questions');
        setCurrentQuestion(null);  // No customer message to show
        setFirstMessageReceived(true);  // Enable input so candidate can greet
        if (lastMessage.examTimeRemaining) {
          setTimer(lastMessage.examTimeRemaining);
          timerStartedRef.current = true;
        }
        setTimeout(() => inputRef.current?.focus(), 100);
        break;

      case 'question':
        setPhase('questions');
        setCurrentQuestion({
          id: lastMessage.questionId,
          text: lastMessage.questionText
        });
        setQuestionNumber(lastMessage.questionNumber);
        setTotalQuestions(lastMessage.totalQuestions);
        setFirstMessageReceived(true);
        // Don't overwrite timer — it was set on "Start" click
        if (!timerStartedRef.current) {
          setTimer(lastMessage.examTimeRemaining);
          timerStartedRef.current = true;
        }
        setIsLast(lastMessage.isLast || false);
        // Focus input
        setTimeout(() => inputRef.current?.focus(), 100);
        break;

      case 'resume':
        if (lastMessage.currentQuestion) {
          setPhase('questions');
          setCurrentQuestion({
            id: lastMessage.currentQuestion.questionId,
            text: lastMessage.currentQuestion.questionText
          });
          setQuestionNumber(lastMessage.currentQuestion.questionNumber);
          setTotalQuestions(lastMessage.currentQuestion.totalQuestions);
          setFirstMessageReceived(true);
        } else {
          // Agent-first flow: no current customer question, but session is in questions phase
          setPhase('questions');
        }
        setTimer(lastMessage.examTimeRemaining);
        timerStartedRef.current = true;
        if (lastMessage.scenario) {
          setScenario(lastMessage.scenario);
          if (lastMessage.scenario.customerName) {
            setCustomerName(lastMessage.scenario.customerName);
          }
        }
        // Restore conversation history from server
        if (lastMessage.conversationHistory && lastMessage.conversationHistory.length > 0) {
          const restoredHistory = [];
          const history = lastMessage.conversationHistory;

          // If currentQuestion is set, the last customer message will be shown separately
          // as the "current question" — so exclude it from chatHistory to avoid duplication
          const hasCurrentQuestion = !!lastMessage.currentQuestion;
          const lastCustomerIdx = hasCurrentQuestion
            ? history.map((m, i) => m.role === 'customer' ? i : -1).filter(i => i >= 0).pop()
            : -1;

          for (let i = 0; i < history.length; i++) {
            const msg = history[i];
            // Skip the last customer message if it's going to be shown as currentQuestion
            if (i === lastCustomerIdx) continue;

            if (msg.role === 'customer') {
              restoredHistory.push({ type: 'question', text: msg.content, id: `restored-${restoredHistory.length}` });
            } else if (msg.role === 'agent') {
              restoredHistory.push({ type: 'answer', text: msg.content });
            }
          }
          setChatHistory(restoredHistory);
          if (restoredHistory.length > 0) {
            setFirstMessageReceived(true);
          }
        }
        // Restore draft text
        if (draftRef.current) {
          setInputText(draftRef.current);
        }
        // Focus input for continued typing
        setTimeout(() => inputRef.current?.focus(), 100);
        break;

      case 'timer_warning':
        setTimerWarning(lastMessage.message);
        setTimeout(() => setTimerWarning(''), 5000);
        break;

      case 'time_up':
        setPhase('time_up');
        setCompletionData({
          message: lastMessage.message,
          questionsAnswered: lastMessage.questionsAnswered,
          autoSubmit: lastMessage.autoSubmit
        });
        // If autoSubmit is true (server forced), close connection
        if (lastMessage.autoSubmit) {
          close();
        }
        break;

      case 'session_complete':
        setPhase('complete');
        setCompletionData({
          message: lastMessage.message,
          totalTime: lastMessage.totalTime,
          questionsAnswered: lastMessage.questionsAnswered
        });
        close();
        break;

      case 'error':
        setErrorMessage(lastMessage.message);
        setPhase('error');
        break;

      case 'session_ended':
        setPhase('complete');
        setCompletionData({
          message: lastMessage.message,
          autoSubmitted: true
        });
        break;

      case 'pong':
        // Sync timer from server to keep all session tabs aligned
        if (lastMessage.examTimeRemaining !== undefined) {
          setTimer(lastMessage.examTimeRemaining);
        }
        break;

      case 'new_session_available':
        // Server says a new session is ready — show banner on this tab
        // Only show if not already accepted on another tab
        if (!localStorage.getItem(`session_accepted_${lastMessage.nextSessionId}`)) {
          setNextSessionId(lastMessage.nextSessionId);
          setShowNewSessionPopup(true);
        }
        break;

      default:
        break;
    }
  }, [lastMessage, close]);

  // Timer — local countdown every second, synced from server every 5s via pong
  useEffect(() => {
    if (phase !== 'questions' || timer <= 0) return;

    const interval = setInterval(() => {
      setTimer(prev => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(interval);
  }, [phase, timer > 0]); // eslint-disable-line

  // Reading timer countdown
  useEffect(() => {
    if (phase !== 'reading' || readingTimer <= 0) return;

    const interval = setInterval(() => {
      setReadingTimer(prev => {
        const next = Math.max(0, prev - 1);
        // Auto-start when reading timer reaches 0
        if (next === 0) {
          handleReadyClick();
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [phase, readingTimer]);

  // Auto-scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory, currentQuestion, displayedText]);

  // Typewriter effect for customer messages
  useEffect(() => {
    if (!currentQuestion?.text) {
      setDisplayedText('');
      setIsTyping(false);
      return;
    }

    const fullText = currentQuestion.text;
    setDisplayedText('');
    setIsTyping(true);

    let index = 0;
    const speed = 25; // ms per character

    typewriterRef.current = setInterval(() => {
      index++;
      setDisplayedText(fullText.slice(0, index));
      if (index >= fullText.length) {
        clearInterval(typewriterRef.current);
        setIsTyping(false);
      }
    }, speed);

    return () => {
      if (typewriterRef.current) clearInterval(typewriterRef.current);
    };
  }, [currentQuestion]);

  // Save draft on input change
  const handleInputChange = (e) => {
    setInputText(e.target.value);
    draftRef.current = e.target.value;
  };

  // Handle Enter key to send message
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      if (inputText.trim() && isConnected) {
        handleSubmitAnswer();
      }
    }
  };

  // Handle "Next Question" or "Submit"
  const handleSubmitAnswer = () => {
    if (!inputText.trim()) return;

    if (isLast) {
      // Last question — show confirmation
      setShowConfirmDialog(true);
      return;
    }

    submitAnswer();
  };

  // Handle early submit (before all questions)
  const handleEarlySubmit = () => {
    setShowConfirmDialog(true);
  };

  const submitAnswer = () => {
    // Add to chat history
    if (currentQuestion) {
      setChatHistory(prev => [
        ...prev,
        { type: 'question', text: currentQuestion.text, id: currentQuestion.id },
        { type: 'answer', text: inputText }
      ]);
    } else {
      // Agent-first flow: no customer question yet, just add the agent message
      setChatHistory(prev => [
        ...prev,
        { type: 'answer', text: inputText }
      ]);
    }

    // Send answer via WebSocket
    sendMessage({
      type: 'answer',
      questionId: currentQuestion?.id || `agent-first-${Date.now()}`,
      answer: inputText
    });

    // Clear input
    setInputText('');
    draftRef.current = '';
    setCurrentQuestion(null);
  };

  const handleConfirmSubmit = () => {
    setShowConfirmDialog(false);

    // If there's text in the input, save it as the current answer first
    if (inputText.trim() && currentQuestion) {
      setChatHistory(prev => [
        ...prev,
        { type: 'question', text: currentQuestion.text, id: currentQuestion.id },
        { type: 'answer', text: inputText }
      ]);

      // Send answer
      sendMessage({
        type: 'answer',
        questionId: currentQuestion.id,
        answer: inputText
      });
    }

    // Send submit — retry if WebSocket not ready
    const sent = sendMessage({ type: 'submit' });
    if (!sent) {
      // WebSocket not open — retry after short delay
      setTimeout(() => {
        sendMessage({ type: 'submit' });
      }, 1000);
    }

    setInputText('');
    draftRef.current = '';
  };

  const handleReadyClick = () => {
    const sent = sendMessage({ type: 'ready' });
    if (sent) {
      setPhase('questions');
      timerStartedRef.current = false;
    } else {
      // WebSocket not connected — retry after reconnect
      console.warn('[SessionPage] ready message not sent — WebSocket not connected, retrying...');
      const retryInterval = setInterval(() => {
        const retrySent = sendMessage({ type: 'ready' });
        if (retrySent) {
          clearInterval(retryInterval);
          setPhase('questions');
          timerStartedRef.current = false;
        }
      }, 1000);
      // Stop retrying after 30 seconds
      setTimeout(() => clearInterval(retryInterval), 30000);
    }
  };

  // Format seconds to mm:ss
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // --- RENDER ---

  // Connection lost overlay (only show after WebSocket was connected and then lost)
  const [showDisconnect, setShowDisconnect] = useState(false);
  const wasConnectedRef = useRef(false);

  useEffect(() => {
    if (isConnected) {
      wasConnectedRef.current = true;
      setShowDisconnect(false);
    } else if (wasConnectedRef.current && phase !== 'complete' && phase !== 'error') {
      // Only show overlay if we were previously connected
      const timer = setTimeout(() => setShowDisconnect(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [isConnected, phase]);

  const connectionOverlay = showDisconnect && (
    <div className="connection-overlay">
      <div className="connection-modal">
        <h2>⚠️ Connection Lost</h2>
        <p>Reconnecting...</p>
        <p>Your progress is saved. Do not close this tab.</p>
        <p className="reconnect-info">Attempt {reconnectAttempt}...</p>
      </div>
    </div>
  );

  // Time's up — show submit button
  if (phase === 'time_up') {
    const handleTimeUpSubmit = () => {
      sendMessage({ type: 'submit' });
      setPhase('complete');
      setCompletionData({
        ...completionData,
        message: 'Session submitted after time expired.'
      });
      close();
    };

    return (
      <div className="session-page">
        <div className="time-up-screen">
          <div className="time-up-icon">⏰</div>
          <h1>Time's Up!</h1>
          <p>Your 40-minute assessment time has ended.</p>
          <p>Interactions completed in this session: <strong>{completionData?.questionsAnswered || 0}</strong></p>
          <p className="time-up-hint">Click Submit to save your answers for this session.</p>
          <button className="time-up-submit-btn" onClick={handleTimeUpSubmit}>
            Submit Session ✓
          </button>
        </div>
      </div>
    );
  }

  // Loading
  if (phase === 'loading') {
    return (
      <div className="session-page">
        <div className="loading">
          <p>Starting session {sessionId}...</p>
        </div>
      </div>
    );
  }

  // Error
  if (phase === 'error') {
    return (
      <div className="session-page">
        <div className="error-card">
          <h2>Error</h2>
          <p>{errorMessage}</p>
          <button className="ready-button" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Complete
  if (phase === 'complete') {
    return (
      <div className="session-page">
        <div className="completion-card">
          <h2>✅ Session {sessionId} Complete</h2>
          {completionData?.autoSubmitted && (
            <p className="auto-submit-note">
              {completionData.message || 'Session was auto-submitted.'}
            </p>
          )}
          {completionData?.totalTime && (
            <p>Time taken: {formatTime(completionData.totalTime)}</p>
          )}
          {completionData?.questionsAnswered && (
            <p>Interactions: {completionData.questionsAnswered}</p>
          )}
          {!embedded && <p>You may close this tab.</p>}
        </div>
      </div>
    );
  }

  // Reading phase
  if (phase === 'reading') {
    return (
      <div className="session-page">
        {connectionOverlay}

        {/* New Session Banner — also show during reading phase */}
        {showNewSessionPopup && !localStorage.getItem(`session_accepted_${nextSessionId}`) && (
          <div className="new-session-banner">
            <span className="new-session-banner-icon">🔔</span>
            <span className="new-session-banner-text">
              Customer {nextSessionId} is waiting for assistance!
            </span>
            <button className="accept-session-banner-btn" onClick={handleAcceptNewSession}>
              Accept ↗
            </button>
          </div>
        )}

        <div className="reading-phase">
          <div className="reading-header">
            <h2>Session {sessionId} — How to Give Assessment</h2>
            <div className="reading-timer">⏱ {formatTime(readingTimer)}</div>
          </div>
          <div className="scenario-content instructions-reading">
            <h3>Please read the following guidelines before you start:</h3>

            <section className="instructions-section">
              <h4>🧹 Before You Start</h4>
              <ul className="tips-list">
                <li>Use a stable internet connection and a laptop/desktop (not mobile)</li>
                <li>Ensure you have at least 45 minutes of uninterrupted time</li>
              </ul>
            </section>

            <section className="instructions-section">
              <h4>📋 Overview</h4>
              <p>You will handle real customer conversations. Each session presents a scenario where you respond to a customer as you would in a real call. You have 3 sessions, each with a different customer scenario.</p>
            </section>

            <section className="instructions-section">
              <h4>⏱️ Time &amp; Sessions</h4>
              <ul className="warning-list">
                <li>You have a total of <strong>40 minutes</strong> shared across all 3 sessions</li>
                <li>The timer starts when you click "Start" on your first session</li>
                <li><strong>Attending all 3 sessions is mandatory</strong></li>
                <li><strong>You must pass all 3 sessions</strong> to receive an overall Pass</li>
                <li>Skipping any session will result in a Fail</li>
              </ul>
            </section>

            <section className="instructions-section">
              <h4>🔄 Starting Next Sessions</h4>
              <ul className="tips-list">
                <li>Session 2 and 3 will appear as pop-ups during your active session — click to accept</li>
                <li>If you finish a session before the pop-up appears, go back to the <strong>Assessment Portal (Hub)</strong> — the next session will show "Ready to start" once available</li>
              </ul>
            </section>

            <section className="instructions-section">
              <h4>🔌 If You Get Disconnected</h4>
              <ul className="warning-list">
                <li>If your internet drops, the system pauses your session</li>
                <li>If you reconnect <strong>within 10 minutes</strong>, your exam resumes from where you left off</li>
                <li>After 10 minutes, the session is <strong>auto-submitted</strong> with whatever answers you've given</li>
              </ul>
            </section>

            <section className="instructions-section">
              <h4>🔄 If You Close the Tab by Mistake</h4>
              <ul className="tips-list">
                <li>Don't panic — go back to the <strong>Assessment Portal (Hub)</strong></li>
                <li>Click on your in-progress session to resume</li>
                <li>Your conversation history and progress are saved</li>
              </ul>
            </section>

            <section className="instructions-section">
              <h4>✅ Do's</h4>
              <ul className="do-list">
                <li>Read the scenario carefully before clicking Start</li>
                <li>Respond naturally as if you are speaking to a real customer</li>
                <li>Be polite, professional, and empathetic in your responses</li>
                <li>Use proper grammar and spelling in your answers</li>
                <li>Take your time to read each customer message before replying</li>
                <li>Submit your session when you are done or when all interactions are complete</li>
              </ul>
            </section>

            <section className="instructions-section">
              <h4>❌ Don'ts</h4>
              <ul className="dont-list">
                <li>Do not copy-paste answers — copy and paste is disabled</li>
                <li>Do not close the session tab during the assessment</li>
                <li>Do not use any external tools, translators, or assistants</li>
                <li>Do not share your login credentials with anyone</li>
                <li>Do not leave the assessment idle for long periods</li>
                <li>Do not refresh the page unnecessarily during the session</li>
              </ul>
            </section>

            <section className="instructions-section">
              <h4>⚠️ Important Notes</h4>
              <ul className="warning-list">
                <li>Each session evaluates you on 6 communication parameters including grammar and spelling</li>
                <li>Once a session is submitted, you cannot go back to modify your answers</li>
                <li>Write clearly and completely — your responses will be reviewed by AI</li>
              </ul>
            </section>

            <section className="instructions-section">
              <h4>💡 Tips</h4>
              <ul className="tips-list">
                <li>Greet the customer warmly at the start of each session</li>
                <li>Address all parts of the customer's query in your response</li>
                <li>Keep your responses concise but complete</li>
                <li>Use a professional tone throughout the conversation</li>
                <li>Proofread your response before sending</li>
              </ul>
            </section>
          </div>
          <div className="reading-actions">
            <p className="reading-hint">Read the guidelines above. Click Start when ready.</p>
            <button className="ready-button" onClick={handleReadyClick}>
              Start ▶
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Questions phase (chat interface)
  return (
    <div className="session-page">
      {connectionOverlay}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="confirm-overlay">
          <div className="confirm-dialog">
            <h2>⚠️ Submit Session?</h2>
            <p>You have completed {questionNumber - 1} interaction{questionNumber - 1 !== 1 ? 's' : ''} so far.</p>
            <p>Once submitted, you cannot go back or change your answers.</p>
            <p><strong>Are you sure you want to submit?</strong></p>
            <div className="confirm-actions">
              <button className="cancel-btn" onClick={() => setShowConfirmDialog(false)}>
                Cancel
              </button>
              <button className="confirm-btn" onClick={handleConfirmSubmit}>
                Confirm Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Session Banner (non-blocking — at top) */}
      {showNewSessionPopup && !localStorage.getItem(`session_accepted_${nextSessionId}`) && (
        <div className="new-session-banner">
          <span className="new-session-banner-icon">🔔</span>
          <span className="new-session-banner-text">
            Customer {nextSessionId} is waiting for assistance!
          </span>
          <button className="accept-session-banner-btn" onClick={handleAcceptNewSession}>
            Accept ↗
          </button>
        </div>
      )}

      {/* Timer warning */}
      {timerWarning && (
        <div className="timer-warning-toast">{timerWarning}</div>
      )}

      {/* Header */}
      <div className="session-header">
        <div className="session-info">
          <h2>Session {sessionId}{scenario ? ` — ${scenario.title}` : ''}</h2>
          <span className="question-counter">Q{questionNumber}</span>
        </div>
        <div className={`exam-timer ${timer <= 60 ? 'critical' : timer <= 300 ? 'warning' : ''}`}>
          {timer > 0 ? `⏱ ${formatTime(timer)}` : ''}
        </div>
      </div>

      {/* Chat area */}
      <div className="chat-area">
        {/* Waiting indicator — shown while LLM generates opening customer message */}
        {chatHistory.length === 0 && !currentQuestion && phase === 'questions' && (
          <div className="chat-hint" style={{
            textAlign: 'center',
            padding: '20px',
            color: '#666',
            fontSize: '14px',
            fontStyle: 'italic',
            marginTop: '40px'
          }}>
            <p>💬 A customer is connected. Please greet them to begin the conversation.</p>
          </div>
        )}

        {/* Previous Q&As */}
        {chatHistory.map((item, idx) => (
          <div key={idx} className={`chat-bubble ${item.type}`}>
            {item.type === 'question' && <span className="bubble-label">🧑 {customerName}:</span>}
            {item.type === 'answer' && <span className="bubble-label">🧑‍💻 You:</span>}
            <p>{item.text}</p>
          </div>
        ))}

        {/* Current question */}
        {currentQuestion && (
          <div className="chat-bubble question current">
            <span className="bubble-label">🧑 {customerName}:</span>
            <p>{displayedText}{isTyping && <span className="typewriter-cursor">|</span>}</p>
          </div>
        )}

        {/* Typing indicator when waiting for LLM (only show after agent has sent at least one message) */}
        {!currentQuestion && phase === 'questions' && chatHistory.some(item => item.type === 'answer') && (
          <div className="chat-bubble question typing">
            <span className="bubble-label">🧑 {customerName}:</span>
            <p className="typing-indicator">
              <span className="dot"></span>
              <span className="dot"></span>
              <span className="dot"></span>
            </p>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>

      {/* Input area */}
      <div className="input-area">
        <div className="input-box-wrapper">
          <textarea
            ref={inputRef}
            className="answer-input"
            placeholder={firstMessageReceived ? "Type your response..." : "Waiting for customer message..."}
            value={inputText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onPaste={(e) => e.preventDefault()}
            onCopy={(e) => e.preventDefault()}
            onCut={(e) => e.preventDefault()}
            disabled={!isConnected || !firstMessageReceived}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            data-gramm="false"
            data-gramm_editor="false"
            data-enable-grammarly="false"
          />
          <button
            className="send-btn-circle"
            onClick={handleSubmitAnswer}
            disabled={!isConnected || !firstMessageReceived || !inputText.trim()}
            title="Send"
          >
            ➤
          </button>
        </div>
        <div className="submit-session-wrapper">
          <button
            className="submit-early-btn"
            onClick={handleEarlySubmit}
            disabled={!isConnected}
          >
            Submit Session
          </button>
        </div>
      </div>
    </div>
  );
}

export default SessionPage;
